import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";
import BatchSlider from "../components/batchSlider";
import Table from "../components/Table";
import LoadingPopup from "../components/LoadingPopup";
import Footer from "../components/Footer";
import NotAvailable from "../components/NotAvailable";
import { AppContext } from "../context/AppContext";
import { Helmet } from "react-helmet";
import { useFetchBatchesQuery } from "../store/apis/api";

function ResultsHighLevel() {
    const { batchRefreshKey } = useContext(AppContext); 
    const [noBatches, setNoBatches] = useState(false);
   
    const links = [
        { name: "Home", link: "/landing" , isActive: true},
        { name: "Upload new images", link: "/upload", isActive:false },
        { name: "View analysis", link: "/resultsHighLevel", isActive:false },
        { name: "About", link: "/about", isActive: false},
        { name: "Help", link: "/help", isActive: false}
        // { name: "Contact us", link: "/contactUs", isActive:false },
      ];

    const { data, error, isLoading, refetch } = useFetchBatchesQuery(batchRefreshKey);

    useEffect(() => {
        if (window.__REACT_SNAP__) return;
        refetch(); // Always refresh when page is entered
    }, []);      // runs once per mount


    useEffect(() => {
        if (window.__REACT_SNAP__) return;
        if (batchRefreshKey > 0) {
        refetch(); // 👈 auto reload when triggered globally
        }
    }, [batchRefreshKey, refetch]);
    
    // Extract batches and images from fetched data
    const batches = data?.batches || [];
    const imgs = data?.imgs || [];

    // Set noBatches state if there are no batches
    useEffect(() => {
        if (window.__REACT_SNAP__) return;
        if (!isLoading && batches.length === 0) {
        setNoBatches(true);
        } else {
        setNoBatches(false);
        }
    }, [isLoading, batches]);

    // Transform batch data for table
    const tableData = batches.map(batch => ({
        Name: batch.batch_name,
        Timestamp: batch.timestamp,
        Rows: batch.rows,
        Columns: batch.columns,
        "# Modules": `${batch.stats["# Modules"][0]} / ${batch.NumModules}`,
        "# Cells": `${batch.stats["# Cells"][0]} / ${batch.NumCells}`
    }));

    // Transform images for batch slider
    const batchCar = imgs.map((img, i) => ({
        name: batches[i]?.batch_name,
        image: img,
        numModules: batches[i]?.NumModules
    }));

    return (    
        <Container>
            <Helmet>
                <title>View Analysis | SolarAIDD</title>
                <meta name="description" content="Analyze heatmaps of all uploaded batches with defective modules at cell level. Get insights into which modules have defects and where they are located."/>
                <meta name="robots" content="noindex, nofollow" />
                <link rel="canonical" href="https://solaraidd.csir.co.za/resultsHighLevel" />
            </Helmet>
            <Navbar links={links} isScrolled={true} />

            {isLoading && <LoadingPopup />}

            {!isLoading && (noBatches || error) && (<NotAvailable/>)}
            
            {!isLoading && !noBatches &&  !error && (
            <div className="container">
                <div className="title">
                    <h1> Overview of the Quality Analysis </h1>
                    <div className="sub-title">
                        <h4> Welcome to the results section. Here, you can see an overview of the quality analysis for each of the batches you uploaded.  </h4>
                    </div>
                </div>  
                <div className="batch-info">         
                    <div className="table-container">
                        <AnimatePresence>
                            <motion.div
                                key={batchRefreshKey} // force animation on refresh
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <Table title={"Overview of the defectes detected for the batches"} data={tableData} page={1} />
                            </motion.div>
                        </AnimatePresence>  
                    </div>
                    <div className="subtitle">
                        <h2> Heatmaps showing the most defective cells for the batches </h2>
                        <div className="description">
                        <h4> The heatmaps show the number of modules in the batch which have a defective cell in the corresponding row and column number.
                            Since each module in a batch has the same number of cell rows and columns, we can inspect the cell locations and see which row
                            and column number is most likely to contain defects for all modules in the specific batch.
                        </h4>
                        </div>                
                    </div>
                    <AnimatePresence>
                        <motion.div
                            key={batchRefreshKey}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }}
                            >
                            <BatchSlider data={batchCar} />
                        </motion.div>
                    </AnimatePresence>
                    {/* <BatchSlider data={batchCar} /> */}
                </div> 
                <Footer/>
            </div>          
            )}         

        </Container>
    );
}

export default ResultsHighLevel;

const Container = styled.div`
background-color: white;
.container {
    position: absolute;
    top: 3rem;
    left: 0;
    width: 100vw;
    grid-template-rows: 15vh 85vh;
    color: black;
    background: white;
    .title {
        text-align: center;  
        padding: 3rem;
        .sub-title {
            padding: 2rem;
        }
    }
    .batch-info {
        background: #d2d9dd;
        padding: 3rem;
        gap: 2rem;        
        .table-container {
            display: flex;        
            align-items: center;
            justify-content: center;            
            background: #d2d9dd;
        }        
        .subtitle {
            text-align: center;
            padding: 2rem;
            .description {
                padding: 2rem;
            }
        }
    }
    .module-info {
        .subtitle {
            text-align: center;
            padding: 2rem;
        }        
    }
}
`;